// Customer languages: what an organisation offers, in order (first = default),
// and how a multilingual text is read. Mirrors internal/i18n/settings.go.
import { LANGUAGES, SOURCE_LANGUAGE, type LanguageCode, type LanguageInfo } from './languages'

/** What every organisation offered before languages were a setting, English first (migration 0139). */
export const LEGACY_CUSTOMER: readonly LanguageCode[] = ['en', 'az', 'ru']

export function languageInfo(code: LanguageCode): LanguageInfo {
  return LANGUAGES.find((l) => l.code === code) ?? { code, name: code, native: code }
}

/** A language's own name, as a language button shows it. */
export const nativeName = (code: LanguageCode): string => languageInfo(code).native

/** The offered list, or English alone when none was sent. */
export function offered(list: readonly LanguageCode[] | undefined | null): LanguageCode[] {
  return list && list.length > 0 ? [...list] : [SOURCE_LANGUAGE]
}

/** The customer default: the first offered language. */
export const firstLanguage = (list: readonly LanguageCode[] | undefined | null): LanguageCode => offered(list)[0]!

/** asked when offered, else the default. */
export function pickLanguage(asked: string | undefined | null, list: readonly LanguageCode[] | undefined | null): LanguageCode {
  const all = offered(list)
  const code = (asked ?? '').trim().toLowerCase()
  return all.includes(code) ? code : all[0]!
}

/** The first browser language that is offered, if any. */
export function browserPick(preferred: readonly string[], list: readonly LanguageCode[]): LanguageCode | undefined {
  for (const tag of preferred) {
    const code = tag.toLowerCase().split('-')[0] ?? ''
    if (list.includes(code)) return code
  }
  return undefined
}

/** Fallback order: requested, the organisation's default, English. */
export function fallbackOrder(requested: string, orgDefault?: string): string[] {
  const out: string[] = []
  for (const c of [requested, orgDefault ?? '', SOURCE_LANGUAGE]) if (c && !out.includes(c)) out.push(c)
  return out
}

/** A text in requested, else the org default, else English, else any (known order first). */
export function resolveText(text: Record<string, string> | undefined | null, requested: string, orgDefault?: string): string {
  if (!text) return ''
  for (const c of fallbackOrder(requested, orgDefault)) {
    const v = text[c]?.trim()
    if (v) return v
  }
  for (const l of LANGUAGES) {
    const v = text[l.code]?.trim()
    if (v) return v
  }
  for (const c of Object.keys(text).sort()) {
    const v = text[c]?.trim()
    if (v) return v
  }
  return ''
}

/** The voice a language is spoken in: az, ru and en are recorded; others use the machine's. */
export function voiceLanguage(code: LanguageCode): 'az' | 'ru' | 'en' | undefined {
  return code === 'az' || code === 'ru' || code === 'en' ? code : undefined
}
